import { useProfileContext } from '@components/Contexts/ProfileContext';
import _ from 'lodash';
import { useMemo, useCallback } from 'react';
import { PROFILE_PAGE_SECTIONS } from '@utils/constants';
import { getReviewDataFromProjects } from '../ProjectsReviews/ProjectsReviewsUtils';

export interface ProfileSection {
  section: string;
  visible: boolean;
  showInNavbar: boolean;
}

interface SectionConfig {
  section: string;
  visible: boolean;
  navBarVisible: boolean;
}

export const useProfileSections = (numberOfTemplates: number) => {
  const { partnerProfile, offeredServices } = useProfileContext();

  const sections = useMemo(() => {
    const shouldShowClientProjects =
      (partnerProfile.portfolio?.items || []).length > 0;
    const shouldShowServices = (offeredServices || []).length > 0;
    const shouldShowReviews =
      getReviewDataFromProjects(partnerProfile.projects || []).length > 0;
    const shouldShowTemplates =
      numberOfTemplates > 0 &&
      !partnerProfile.displayConfiguration?.hideTemplatesSection;

    const sectionConfig: SectionConfig[] = [
      {
        section: PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS,
        visible: shouldShowClientProjects,
        navBarVisible: shouldShowClientProjects,
      },
      {
        section: PROFILE_PAGE_SECTIONS.ABOUT,
        visible: true,
        navBarVisible: true,
      },
      {
        section: PROFILE_PAGE_SECTIONS.SERVICES,
        visible: shouldShowServices,
        navBarVisible: false,
      },
      {
        section: PROFILE_PAGE_SECTIONS.REVIEWS,
        visible: shouldShowReviews,
        navBarVisible: false,
      },
      {
        section: PROFILE_PAGE_SECTIONS.SERVICES_AND_REVIEWS,
        visible: shouldShowServices || shouldShowReviews,
        navBarVisible: shouldShowServices || shouldShowReviews,
      },
      {
        section: PROFILE_PAGE_SECTIONS.TEMPLATES,
        visible: shouldShowTemplates,
        navBarVisible: shouldShowTemplates,
      },
    ];

    // Page sections (excluding navbar-only sections)
    const pageSections = sectionConfig.filter(
      (s) => s.section !== PROFILE_PAGE_SECTIONS.SERVICES_AND_REVIEWS,
    );

    // Navbar sections - simple filter
    const navbarSections = sectionConfig.filter(
      (s) => s.navBarVisible && s.visible,
    );

    // Last visible page section
    const lastVisibleSection = pageSections
      .filter((s) => s.visible)
      .pop()?.section;

    return {
      shouldShowClientProjects,
      shouldShowServices,
      shouldShowReviews,
      shouldShowTemplates,
      shouldShowServicesAndReviews: shouldShowServices || shouldShowReviews,
      haveReviews: shouldShowReviews,

      sectionConfig,
      navbarSections,

      isLastSection: {
        clientProjects:
          lastVisibleSection === PROFILE_PAGE_SECTIONS.CLIENT_PROJECTS,
        about: lastVisibleSection === PROFILE_PAGE_SECTIONS.ABOUT,
        services: lastVisibleSection === PROFILE_PAGE_SECTIONS.SERVICES,
        reviews: lastVisibleSection === PROFILE_PAGE_SECTIONS.REVIEWS,
        templates: lastVisibleSection === PROFILE_PAGE_SECTIONS.TEMPLATES,
      },
    };
  }, [
    partnerProfile.portfolio?.items,
    offeredServices,
    partnerProfile.projects,
    numberOfTemplates,
  ]);

  const getSectionByKey = useCallback(
    (sectionKey: string) => {
      return sections.sectionConfig.find((s) => s.section === sectionKey);
    },
    [sections.sectionConfig],
  );

  const isSectionVisible = useCallback(
    (sectionKey: string) => {
      return getSectionByKey(sectionKey)?.visible ?? false;
    },
    [getSectionByKey],
  );

  const isNavbarSectionVisible = useCallback(
    (sectionKey: string) => {
      const section = getSectionByKey(sectionKey);
      return section?.visible && section?.navBarVisible;
    },
    [getSectionByKey],
  );

  const getFirstVisibleNavbarSection = useCallback(() => {
    return sections.navbarSections[0]?.section ?? PROFILE_PAGE_SECTIONS.ABOUT;
  }, [sections.navbarSections]);

  const getValidActiveTab = useCallback(
    (activeTab?: string) => {
      if (activeTab && isNavbarSectionVisible(activeTab)) {
        return activeTab;
      }
      return getFirstVisibleNavbarSection();
    },
    [isNavbarSectionVisible, getFirstVisibleNavbarSection],
  );

  return {
    ...sections,
    // Utility functions
    getSectionByKey,
    isSectionVisible,
    isNavbarSectionVisible,
    getFirstVisibleNavbarSection,
    getValidActiveTab,
  };
};
